<template>
	<VueApexChart
	    v-if="chartOptions"
	    ref="chart"
	    class="app-chart-line"
	    :class="{ 'live' : autoUpdate }"
	    width="100%"
	    height="100%"
	    :options="chartOptions"
	    :series="series"
	/>
</template>

<script>
export default {
	props: {
		strokeColor: {
			type: String,
			default: '#54D8FF'
		},

		// define the width of y-axis labels
		yAxisWidth: {
			type: Number,
			default: 60
		},

		sessionStart: {
			type: [String, Date],
			default: () => new Date()
		},

		sessionEnd: {
			type: [String, Date],
			default: () => new Date()
		},

		labels: {
			type: Array,
			default: () => []
		},

		series: {
			type: Array,
			default: () => []
		},

		annotations: {
			type: Object,
			default: () => {}
		},

		autoUpdate: {
			type: Boolean,
			default: false
		},

		// define the visible minutes
		xAxisRange: {
			type: Number,
			default: 60
		},

		// define minimum value for x-axis if no data is provided
		xMinValue: {
			type: [String, Date],
			default: () => new Date()
		},

		toolbar: {
			type: Boolean,
			default: true
		},

		formatTooltip: {
			type: Boolean,
			default: true
		},

		showStartToEnd: {
			type: Boolean,
			default: false
		},

		title: String,
		tooltipTitle: String,
		noData: {
			type: String,
			default: 'No Data'
		},

		type: String
	},

	watch: {
		labels (newVal, oldVal) {
			if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
				this.init()
			}
		},

		series () {
			this.init()
		},

		annotations () {
			this.init()
		}

		// series (newVal, oldVal) {
		// 	if (oldVal.length === 0 && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
		// 		let yTick = this.getYTick()
		// 		this.chartOptions = {...this.chartOptions,
		// 			...{
		// 				yaxis: {
		// 					seriesName: yTick.seriesName,
		// 					tickAmount: yTick.tickAmount,
		// 					min: yTick.min,
		// 					max: yTick.max,
		// 					labels: {
		// 						formatter: (value, index) => this.labels.length ? this.labels[index] : value,
		// 						style: { fontSize: '10px' },
		// 						minWidth: this.yAxisWidth
		// 					}
		// 				}
		// 			}}
		// 	}
		// }
	},

	async created () {
		// add watcher for render related props
		this.$watch(vm => [
			vm.yAxisWidth,
			vm.autoUpdate,
			vm.xAxisRange,
			vm.xMinValue,
			vm.toolbar,
			vm.formatTooltip,
			vm.title,
			vm.tooltipTitle,
			vm.noData
		], async () => {
			this.chartOptions = null
			await this.init()
			if (!this.autoUpdate) { this.setDefaultZoom() }
		})

		await this.init()
		if (!this.autoUpdate) {
			this.setDefaultZoom()
		}
	},

	data () {
		return {
			chartOptions: null,

			modeColors: [
				'#22B1A3', // deep
				'#4A6FF2', // light
				'#FECD74', // rem
				'#CBE4FE' // awake
			]
		}
	},

	computed: {
		rangeInMinutes () {
			return this.xAxisRange * 60000
		}
	},

	methods: {
		getXTick () {
			if (this.series.length && this.series[0].data && this.series[0].data.length) {
				const data = this.series[0].data
				// const min = new Date(data[0][0]).getTime()
				const max = new Date(data[data.length - 1][0]).getTime()
				// const diff = max - min
				// const isBased = diff <= this.rangeInMinutes
				return {
					// min: isBased ? min : max - this.rangeInMinutes,
					min: max - this.rangeInMinutes,
					max: max
				}
			}

			return {
				min: this.xMinValue.getTime(),
				max: this.xMinValue.getTime() + this.rangeInMinutes
			}
		},

		getYTick () {
			const length = this.labels.length
			if (length) {
				if (typeof this.labels[0] === 'string') {
					return {
						seriesName: this.series.length ? this.series[0].name : undefined,
						tickAmount: length - 1,
						min: 0,
						max: length - 1
					}
				}
				if (typeof this.labels[0] === 'number') {
					return {
						seriesName: this.series.length ? this.series[0].name : undefined,
						tickAmount: length - 1,
						min: 0,
						max: this.labels[length - 1]
					}
				}
			}

			return {
				seriesName: this.series.length ? this.series[0].name : undefined,
				tickAmount: undefined,
				min: 0,
				max: undefined
			}
		},

		async init () {
			const xTick = await this.getXTick()
			const yTtick = await this.getYTick()

			this.chartOptions = {
				chart: {
					type: 'line',
					toolbar: {
						show: !this.autoUpdate && this.toolbar,
						tools: {
							download: false,
							reset: false,
							customIcons: [{
								icon: '<span style="font-size: 16px; margin-left: 5px;">&times;</span>',
								index: 4,
								title: 'Reset',
								class: 'custom-icon',
								click: () => {
									this.setDefaultZoom()
								}
							}]
						},
						autoSelected: 'pan'
					},
					animations: {
						easing: 'linear',
						speed: 500,
						animateGradually: { delay: 500 },
						dynamicAnimation: { speed: 500 }
					}
				},
				annotations: this.annotations,
				title: {
					text: this.title,
					margin: 0,
					style: {
						color: 'currentColor',
						fontWeight: 'normal',
						fontSize: '11px',
						fontFamily: 'Quicksand, sans-serif'
					}
				},
				dataLabels: { enabled: false },
				stroke: {
					width: 2,
					curve: 'smooth',
					colors: this.strokeColor
				},
				markers: {
					size: 2,
					colors: [this.strokeColor],
					strokeColor: this.strokeColor,
					hover: { sizeOffset: 1 }
				},
				grid: {
					background: '#fff',
					borderColor: '#eee',
					padding: { left: 15, bottom: 15 }
				},

				tooltip: {
					enabled: !this.autoUpdate,
					fixed: { enabled: true, offsetY: -20 },
					style: { fontSize: '12px' },
					marker: { show: false },
					x: {
						show: true,
						format: 'MMM dd, yyyy hh:mm TT'
					},
					y: {
						formatter: (val) => {
							if (!this.formatTooltip || !this.labels.length) return val
							return this.labels[val]
						},
						title: { formatter: () => this.tooltipTitle }
					}
				},
				xaxis: {
					type: 'datetime',
					labels: {
						datetimeUTC: false, // true to use UTC
						datetimeFormatter: {
							hour: 'h TT',
							minute: 'h:mm TT',
							second: 'h:mm:00 TT'
						},
						style: { fontSize: '10px' }
					},
					tooltip: { enabled: false }
				},
				yaxis: {
					seriesName: yTtick.seriesName,
					tickAmount: yTtick.tickAmount,
					min: yTtick.min,
					max: yTtick.max,
					labels: {
						formatter: (value, index) => this.labels.length ? this.labels[index] : value,
						style: { fontSize: '10px' },
						minWidth: this.yAxisWidth
					}
				},
				noData: {
					text: this.noData,
					align: 'center',
					verticalAlign: 'middle',
					style: { fontSize: '10px' }
				}
			}

			if ((this.autoUpdate && this.annotations.xaxis.length > 0) || (this.autoUpdate && this.series.length > 0)) {
				this.chartOptions = {...this.chartOptions,
					...{
						xaxis: {
							min: xTick.min,
							max: xTick.max,
							range: this.rangeInMinutes,
							type: 'datetime',
							labels: {
								datetimeUTC: false, // true to use UTC
								datetimeFormatter: {
									hour: 'h TT',
									minute: 'h:mm TT',
									second: 'h:mm:00 TT'
								},
								style: { fontSize: '10px' }
							},
							tooltip: { enabled: false }
						}
					}}
			}

			// if sleep data change the marker colors based on value
			if (this.type === 'sleep session' && this.series.length > 0) {
				this.chartOptions = {...this.chartOptions,
					...{
						markers: {
							size: 4,
							hover: { size: 4 },
					        strokeColor: 'transparent',
							// 🎯 Use discrete markers to color them individually
							discrete: this.series[0].data.map((val, index) => {
								let color = this.modeColors[val[1]]

								return {
									seriesIndex: 0,
									dataPointIndex: index,
									fillColor: color,
					                strokeColor: 'transparent',
									size: 4
								}
							})
						},
						tooltip: {
							enabled: !this.autoUpdate,
							shared: false,
							style: { fontSize: '12px' },
							marker: { show: false },
							x: {
								show: true,
								format: 'MMM dd, yyyy hh:mm TT'
							},
							y: {
								formatter: (val) => {
									if (!this.formatTooltip || !this.labels.length) return val
									return this.labels[val]
								},
								title: { formatter: () => this.tooltipTitle }
							}
						}
					}
				}
			}
		},

		setDefaultZoom () {
			if (this.type === 'sleep session') return
			if (!this.series.length || !this.series[0].data.length) return

			if (this.showStartToEnd) {
				const seriesData = this.series[0].data
				const lastData = new Date(seriesData[seriesData.length - 1][0]).getTime()
				const startData = new Date(seriesData[0][0]).getTime()
				this.$refs.chart.zoomX(startData, lastData)
				return
			}

			if (this.sessionStart !== new Date()) {
				this.$refs.chart.zoomX(new Date(this.sessionStart).getTime(), new Date(this.sessionEnd).getTime())
				return
			}

			const seriesData = this.series[0].data
			const lastData = new Date(seriesData[seriesData.length - 1][0]).getTime()
			const startData = new Date(seriesData[0][0]).getTime()
			const diff = lastData - startData
			const isBased = diff <= this.rangeInMinutes
			const end = lastData
			const start = isBased ? end - this.rangeInMinutes : startData
			this.$refs.chart.zoomX(start, end)
		}
	}
}
</script>

<style lang="scss" scoped>
.app-chart-line {
	width: 100%;
	height: 100%;
	min-height: 0;
	min-width: 0;
	text-transform: capitalize;
	position: relative;

	&.live { pointer-events: none; }
}
</style>



// WEBPACK FOOTER //
// src/components/app-components/AppChartLine.vue